/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable consistent-return */
import React, {
  memo,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from 'react';
import useIsBody from '@wconcept/wconcept-ui-renewal/hooks/useIsBody';
import {
  classNameBind,
  getUniqueKey,
} from '@wconcept/wconcept-common/utils/commonUtils';
import PlayIcon from '@wconcept/wconcept-ui-renewal/components/molecules/_icon/PlayIcon';
import PauseIcon from '@wconcept/wconcept-ui-renewal/components/molecules/_icon/PauseIcon';
import RefreshIcon from '@wconcept/wconcept-ui-renewal/components/molecules/_icon/RefreshIcon';
import LoadingIcon from '@wconcept/wconcept-ui-renewal/components/molecules/_icon/LoadingIcon';
import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import veplayer, {
  IPlayAuthTokenConfig,
  IThumbnailConfig,
} from '@byteplus/veplayer';
import '@byteplus/veplayer/index.min.css';
import { VideoPlayInfo } from '@wconcept/wconcept-common/models/renewal/discovery';
import dynamic from 'next/dynamic';
import * as S from './style';

export const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface IProps {
  active?: boolean;
  videoHiddenChk?: boolean;
  src?: string;
  poster?: string;
  thumbnail?: IThumbnailConfig;
  priority?: boolean;
  className?: string;
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  responsive?: boolean;
  /** adapt : 9:16 비율 이외는 contain 처리 */
  fitType?: 'cover' | 'contain' | 'adapt' | 'none';
  definitionType?: '480p' | '720p' | '1080p';
  muted?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  vid?: string;
  progressShow?: boolean;
  clickControl?: boolean;
  token?: IPlayAuthTokenConfig;
  handleError?: () => void;
  handleEnded?: () => void;
  handleComplete?: (el: any) => void;
  getNewToken?: () => Promise<IPlayAuthTokenConfig>;
  onTimeUpdate?: (time: VideoPlayInfo) => void;
  onPause?: (time: VideoPlayInfo) => void;
}

const VeVideo = ({
  active = true,
  videoHiddenChk = true,
  src = '',
  poster = '',
  thumbnail,
  priority,
  className,
  width,
  height,
  vid,
  token,
  borderRadius,
  responsive = false,
  fitType = 'cover',
  definitionType = '720p',
  muted = false,
  autoplay = false,
  loop = false,
  progressShow = false,
  clickControl = false,
  handleError,
  handleEnded,
  handleComplete,
  getNewToken,
  onTimeUpdate,
  onPause,
}: IProps) => {
  const { isBody } = useIsBody();
  const uniqueKeyRef = useRef(`player-${getUniqueKey()}`);
  const [isPosterShow, setIsPosterShow] = useState(false);
  // const [player, setPlayer] = useState<veplayer | null>(null);
  const player = useRef<veplayer | null>(null);
  /** initPlayer 는 async 라 await 중 active 가 바뀔 수 있다. 세대값으로 무효화한다 */
  const initGeneration = useRef(0);
  const [isAutoplayFail, setIsAutoplayFail] = useState(false);
  const [isClickControl, setIsClickControl] = useState(clickControl);
  const [isPlayerPlay, setIsPlayerPlay] = useState(false);
  const [isVideoPlay, setIsVideoPlay] = useState(false);
  const [isVideoPause, setIsVideoPause] = useState(false);
  const [isVideoRefresh, setIsVideoRefresh] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [$fitType, setFitType] = useState(fitType);
  const [isClient, setIsClient] = useState(false);
  const lastPlayTime = useRef(0);

  const completedCount = useRef(0);

  const viewCount = useRef(0);
  const watchedTime = useRef(0);

  useIsomorphicLayoutEffect(() => {
    setIsClient(true);
  }, []);

  /**
   * className 은 렌더 중에 계산한다.
   * state + useEffect 로 파생하면 클래스가 한 커밋 늦게 붙는다.
   * is-player-play 를 active 와 묶는 것도 같은 이유다.
   * active=false 가 되는 렌더에서 .xgplayer 가 곧바로 언마운트되므로,
   * 클래스도 같은 렌더에서 빠져야 영상과 포스터 사이에 빈 프레임이 생기지 않는다.
   */
  const cName = classNameBind([
    'veVideo',
    progressShow ? 'show-progress' : '',
    active && isPlayerPlay ? 'is-player-play' : '',
    isPosterShow ? 'is-poster-show' : '',
    // isAutoplayFail ? 'is-autoplay-fail' : '',
    isClickControl ? 'is-click-control' : '',
    className || '',
  ]);

  // 포스터 비율 계산
  useEffect(() => {
    if (poster) {
      const img = new Image();
      img.src = poster;
      img.onload = () => {
        if ($fitType === 'adapt') {
          const w = img.width;
          const h = img.height;
          const aspectRatio = w / h;
          if (Math.abs(aspectRatio - 9 / 16) > 0.01) {
            setFitType('contain');
          } else {
            setFitType('cover');
          }
        }

        setIsPosterShow(true);
      };
    }
  }, [poster, $fitType]);

  // progress 재생/멈춤 상태 유지
  const progressBarMoveEnd = useCallback(() => {
    const videoElement = player?.current?.player;
    if (!videoElement) return;
    if (videoElement.paused) {
      setTimeout(() => {
        videoElement.pause();
      }, 50);
      setTimeout(() => {
        videoElement.pause();
      }, 100);
      setTimeout(() => {
        videoElement.pause();
      }, 200);
    }
  }, []);

  // progress touchmove 계산(mo)
  /* const progressBarTouchMove = useCallback(() => {
    const videoElement = player?.current?.player;
    if (!videoElement) return;

    // el
    const playerEl = videoElement.root;
    const trigger = playerEl.querySelector('.trigger');
    const progressBar = playerEl.querySelector('.xgplayer-progress');
    const progressPlayed = playerEl.querySelector(
      '.xgplayer-progress .xgplayer-progress-played'
    );

    // rect
    const prgrssRect = progressPlayed.getBoundingClientRect();
    const trggRect = trigger.getBoundingClientRect();
    const progressRect = progressBar.getBoundingClientRect();

    // 시간 box 위치 계산
    const minX = progressRect.left;
    const maxX = progressRect.right - trggRect.width;
    let posX = prgrssRect.right - trggRect.width / 2;
    posX = Math.max(minX, Math.min(maxX, posX));
    const relativeX = posX - progressRect.left;
    trigger.style.transform = `translateX(${relativeX}px) translateZ(0px)`;
  }, []); */

  // progress touchmove 이벤트 추가(mo)
  /* useEffect(() => {
    const videoElement = player?.current?.player;
    if (!isPlayerPlay || !videoElement || !progressShow) return;

    const progressBar = videoElement.root.querySelector(
      '.xgplayer-mobile .xgplayer-progress'
    );

    if (!progressBar) return;

    progressBar.addEventListener('touchmove', progressBarTouchMove);

    return () => {
      progressBar.removeEventListener('touchmove', progressBarTouchMove);
    };
  }, [isPlayerPlay, progressBarTouchMove, progressShow]); */

  // progress 옵션 재설정(pc)
  useEffect(() => {
    const videoElement = player?.current?.player;
    if (!isPlayerPlay || !videoElement || !progressShow) return;

    if (!videoElement.playerData.isMobile) {
      videoElement.getPlugin('progress').setConfig({
        isCloseClickSeek: false, // 클릭시 바로 탐색
        onMoveStart: progressBarMoveEnd,
      });
    }
  }, [isPlayerPlay, progressBarMoveEnd, progressShow]);

  const handelLoadeStart = useCallback(() => {
    setIsPlayerPlay(false);
  }, []);

  const handleLoadeData = useCallback(
    (e: any) => {
      if (poster) return;
      if ($fitType !== 'adapt') return;

      const $el = e.player.config.el;
      if ($el) {
        const $video = $el.getElementsByTagName('video')[0];
        const w = $video.videoWidth;
        const h = $video.videoHeight;
        const aspectRatio = w / h;
        // console.log(`VeVideo loadeddata 비율: `, w, h, aspectRatio);

        if (Math.abs(aspectRatio - 9 / 16) > 0.01) {
          // console.log('VeVideo loadeddata 9:16 비율이 아닙니다.');
          setFitType('contain');
        } else {
          setFitType('cover');
        }
      }
    },
    [$fitType, poster]
  );

  const onComplete = useCallback(() => {
    handleComplete && handleComplete(player.current);
  }, [handleComplete]);

  const handleTimeUpdate = useCallback(
    (e: any) => {
      const duration = e.duration as number;
      const playTime = e.currentTime as number;
      // 비디오가 끝나기 0.1초 전에 도달했을 때 카운트 증가
      if (duration === 0) return;

      // 시청 시간 누적
      const timeWatched = playTime - lastPlayTime.current;
      if (timeWatched > 0) {
        watchedTime.current += timeWatched;
      }

      // 이전 재생시간보다 현재 시간이 작다면 (리플레이 시작)
      if (playTime < lastPlayTime.current) {
        // 리플레이 시점에 초기화
        lastPlayTime.current = playTime;
        // hasBeenCounted도 필요 없어짐 - 리플레이 때마다 자연스럽게 초기화되니까요
        return;
      }

      // 마지막 0.2초 구간에 진입했고, 이전에는 그 구간이 아니었다면
      if (duration - playTime <= 0.2 && duration - lastPlayTime.current > 0.2) {
        completedCount.current += 1;
      }

      // 시청 시간이 전체 길이와 같거나 클 때만 viewCount 증가
      if (watchedTime.current >= duration) {
        watchedTime.current = 0; // 시청 시간 초기화
        viewCount.current += 1; // viewCount 증가
      }

      lastPlayTime.current = playTime;

      onTimeUpdate &&
        onTimeUpdate({
          contnetTime: duration,
          playTime: watchedTime.current,
          viewCount: viewCount.current,
        });
    },
    [onTimeUpdate]
  );

  const handleOnCanPlay = useCallback(() => {
    setIsVideoLoading(false);
    setIsVideoRefresh(false);
    setIsPlayerPlay(true);
  }, []);

  const handleAutoplayWasPrevented = useCallback(() => {
    // setIsAutoplayFail(true);
    setIsVideoPause(true);
  }, []);

  const handleOnPause = useCallback(
    (e: any) => {
      onPause &&
        onPause({
          contnetTime: e.duration,
          playTime: e.currentTime,
          viewCount: completedCount.current,
        });
    },
    [onPause]
  );

  const handleOnWaiting = useCallback(() => {
    setIsVideoRefresh(false);
    setIsVideoLoading(true);
  }, []);

  const handleOnPlay = useCallback(() => {
    setIsVideoPause(false);
  }, []);

  const handleOnPlaying = useCallback(() => {
    if (muted && player.current) player.current.player.muted = true; // 저전력 소리제어
    setIsVideoPause(false);
  }, [muted]);

  const handleOnEnded = useCallback(() => {
    handleEnded && handleEnded();
  }, [handleEnded]);

  const handleOnError = useCallback(() => {
    setIsVideoLoading(false);
    setIsVideoRefresh(true);
    handleError && handleError();
  }, [handleError]);

  useEffect(() => {
    if (token) {
      completedCount.current = 0;
      viewCount.current = 0;
      watchedTime.current = 0;
    }
  }, [token]);

  const initPlayer = useCallback(async () => {
    if (player.current) return;

    const generation = initGeneration.current;

    try {
      const VePlayerModule = await import('@byteplus/veplayer');
      // await 중 비활성으로 바뀌었으면 만들지 않는다.
      // 만들어 버리면 onDestroy 를 이미 지나쳐서 canplay 로 is-player-play 가 고착된다
      if (generation !== initGeneration.current) return;

      const VePlayer = VePlayerModule.default;

      const newPlayer = new VePlayer({
        id: uniqueKeyRef.current,
        lang: 'en',
        vid,
        url: src,
        width,
        height,
        autoplay,
        loop,
        autoplayMuted: muted,
        streamType: 'hls',
        // alwaysShowDefinition: true,
        enableHlsMSE: true, // 모바일에서 MSE 사용
        useHlsPluginForSafari: true, // Safari에서 HLS 플러그인 사용
        vodLogOpts: {
          line_app_id: 641565,
          tag: 'normal',
        },
        getVideoByToken: {
          ...(token as IPlayAuthTokenConfig),
          defaultDefinition: definitionType,
          // needThumbs: progressShow,
        },
        controls: progressShow,
        progress: {
          isDraggingSeek: false, // 드래그 중 탐색 안 함
          isCloseClickSeek: true, // 클릭 시 바로 탐색 안 함
          onMoveEnd: progressBarMoveEnd,
        },
        thumbnail,
        onTokenExpired: () =>
          new Promise(resolve => {
            // Get a new playAuthToken
            getNewToken?.().then(res => {
              // Return a promise that contains keyToken and playAuthToken
              resolve({
                playAuthToken: res?.playAuthToken, // The new playAuthToken
                keyToken: res?.keyToken, // The new keyToken for playing encrypted HLS video
              });
            });
          }),
      });

      // 생성 도중에도 비활성으로 바뀔 수 있다. 리스너를 붙이기 전에 정리한다
      if (generation !== initGeneration.current) {
        newPlayer.destroy();
        return;
      }

      newPlayer.on('loadstart', handelLoadeStart);

      // 포스터 없는 경우 영상 비율 계산
      newPlayer.on('loadeddata', handleLoadeData);

      newPlayer.on('complete', onComplete);

      newPlayer.on('canplay', handleOnCanPlay);

      newPlayer.on('timeupdate', handleTimeUpdate);

      newPlayer.on('autoplay_was_prevented', handleAutoplayWasPrevented);

      newPlayer.on('pause', handleOnPause);

      newPlayer.once('play', handleOnPlay);

      newPlayer.once('playing', handleOnPlaying);

      newPlayer.on('waiting', handleOnWaiting);

      newPlayer.on('ended', handleOnEnded);

      newPlayer.on('error', handleOnError);
      player.current = newPlayer;
      // setPlayer(newPlayer);
      completedCount.current = 0;

      // Log events
      /* [
        'destroy',
        'error',
        'retry',
        'replay',
        'play_auth_token_expired',
        'urlchange',
        'play_url_change',
        'player_create_finish',
        'loadstart',
        'loadedmetadata',
        'loadeddata',
        'canplay',
        'play',
        'playing',
        'pause',
        'waiting',
        'replay',
        'complete',
        'autoplay_was_prevented',
      ].forEach(eventName => {
        newPlayer.on(eventName, e => {
          console.log('VeVideo eventName => ', eventName, uniqueKeyRef.current);
          // console.log('VeVideo eventName => ', eventName, newPlayer.player);
        });
      }); */
    } catch (error) {
      console.error('Failed to load VePlayer:', error);
      handleError && handleError();
    }
  }, [
    vid,
    src,
    width,
    height,
    autoplay,
    loop,
    thumbnail,
    progressBarMoveEnd,
    muted,
    progressShow,
    token,
    definitionType,
    handelLoadeStart,
    handleLoadeData,
    onComplete,
    handleOnCanPlay,
    handleTimeUpdate,
    handleAutoplayWasPrevented,
    handleOnPause,
    handleOnPlay,
    handleOnPlaying,
    handleOnWaiting,
    handleOnEnded,
    handleOnError,
    getNewToken,
    handleError,
  ]);

  const onDestroy = useCallback(() => {
    completedCount.current = 0;
    // 진행 중인 initPlayer 를 무효화한다(await 중 비활성 전환 대비)
    initGeneration.current += 1;
    // 플레이어가 아직 없어도 클래스는 벗겨야 포스터가 다시 노출된다
    setIsPlayerPlay(false);

    if (player.current && player.current.player) {
      player.current?.player?.off('ended', handleOnEnded);
      player.current?.player?.off('error', handleOnError);
      player.current?.player?.off('complete', onComplete);
      player.current?.player?.off('timeupdate', handleTimeUpdate);
      player.current?.player?.off('canplay', handleOnCanPlay);
      player.current?.player?.off('pause', handleOnPause);
      player.current?.player?.off('play', handleOnPlay);
      player.current?.player?.off('playing', handleOnPlaying);
      player.current?.player?.off('waiting', handleOnWaiting);
      player.current?.player?.off('loadstart', handelLoadeStart);
      player.current?.player?.off('loadedmetadata', handleLoadeData);
      player.current?.player?.off('loadeddata', handleLoadeData);
      player.current?.player?.off(
        'autoplay_was_prevented',
        handleAutoplayWasPrevented
      );
      player.current?.destroy();
      player.current = null;
      completedCount.current = 0;
    }
  }, [
    handelLoadeStart,
    handleAutoplayWasPrevented,
    handleLoadeData,
    handleOnCanPlay,
    handleOnEnded,
    handleOnError,
    handleOnPause,
    handleOnPlay,
    handleOnPlaying,
    handleOnWaiting,
    handleTimeUpdate,
    onComplete,
  ]);

  // click 컨트롤
  const onClickControl = useCallback(() => {
    const videoElement = player?.current?.player;
    if (!videoElement) return;

    if (videoElement.paused) {
      videoElement.play();
      setIsVideoPlay(false);
      setIsVideoPause(true);
      // setIsAutoplayFail(false);
      setTimeout(() => {
        setIsVideoPause(false);
      }, 1000);
    } else {
      videoElement.pause();
      setIsVideoPlay(true);
      setIsVideoPause(false);
      setTimeout(() => {
        setIsVideoPlay(false);
      }, 1000);
    }
  }, [player]);

  // 비디오 hidden 전환 시 일시중지하기
  useEffect(() => {
    if (!videoHiddenChk) return;

    const handleVisibilityChange = () => {
      const videoElement = player?.current?.player;

      if (document.hidden) {
        videoElement?.pause();
      } else {
        videoElement?.play();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [videoHiddenChk]);

  // useEffect(() => {
  // bf캐시 테스트
  // const handleBeforeUnload = () => {
  //   if (player.current?.player) {
  //     player.current.player.pause();
  //   }
  // };
  // window.addEventListener('beforeunload', handleBeforeUnload);
  // return () => {
  //   window.removeEventListener('beforeunload', handleBeforeUnload);
  // };
  // }, []);

  useEffect(() => {
    if (isBody && isClient) {
      // player 는 ref 객체라 늘 truthy 였다. 조건 없이 정리하고 필요할 때만 다시 만든다
      onDestroy();

      if (active) {
        initPlayer();
      }
    }

    return () => {
      onDestroy();
    };
  }, [active, isBody, isClient]);

  const param = useMemo(
    () => ({
      className: cName,
      width,
      height,
      borderRadius,
      $responsive: responsive ? 'true' : 'false',
      $fitType,
      onClick: clickControl && !isVideoRefresh ? onClickControl : undefined,
    }),
    [
      cName,
      width,
      height,
      borderRadius,
      responsive,
      $fitType,
      clickControl,
      isVideoRefresh,
      onClickControl,
    ]
  );

  if (!isClient) {
    return null; // 또는 로딩 인디케이터
  }

  return (
    <S.Box {...param}>
      {active && <S.Video id={uniqueKeyRef.current} $fitType={$fitType} />}
      {poster && (
        <S.Poster className="area-poster" $fitType={$fitType}>
          <Img priority={priority} src={poster} />
        </S.Poster>
      )}
      {isVideoPlay && <PauseIcon size="xl" />}
      {isVideoPause && <PlayIcon size="xl" />}
      {isVideoRefresh && <RefreshIcon size="xl" />}
      {isVideoLoading && <LoadingIcon />}
    </S.Box>
  );
};

export default VeVideo;
